import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';

import { getStudioPropModelObjectUrl } from '../../../../common/hooks/use-content-studio-api.hook';

/**
 * Library props placed in the 3D studio scene — real GLB furniture beside the
 * hero product, so a vendor composes the shot instead of describing it.
 *
 * The blockout system is the ancestor: grey masses the AI replaces with an
 * invented object of that type. A prop is the grown-up version — an actual
 * mesh, so what the capture shows IS the furniture, and the render's job drops
 * from "invent a sofa here" to "make this sofa photoreal". Both coexist: a
 * blockout is still the right tool when the vendor wants the AI's imagination.
 */

export interface PlacedProp {
    id: string;
    propId: string;
    name: string;
    category: string;
    pos: [number, number, number];
    rotY: number;
    scale: number;
}

/**
 * A sensible footprint per category, in metres.
 *
 * The source GLBs are not authored at real-world scale (they come from
 * image-to-3D), and the hero model is normalised to a fixed size too — so a
 * freshly placed prop lands at a size that reads right next to it, and the
 * scale gizmo takes it from there. A category we have not met gets something
 * mid-sized rather than something surprising.
 */
const CATEGORY_SIZE_M: Record<string, number> = {
    sofas: 2.1,
    beds: 2.0,
    'tv stands': 1.7,
    tables: 1.2,
    rugs: 2.0,
    fixtures: 0.9,
    furniture: 1.4,
};

export const propFootprint = (category: string): number => CATEGORY_SIZE_M[category] ?? 1.4;

/**
 * What the placed props are, for the generation prompt.
 *
 * The capture already SHOWS them — they are real meshes in the frame — but the
 * prompt naming each one is what stops the render treating a sofa as backdrop
 * to repaint. Positions are not described: the pixels carry them.
 */
export function describeSceneProps(props: PlacedProp[]): Array<{ name: string; category: string }> {
    return props.map((p) => ({ name: p.name, category: p.category }));
}

/** Loads the decrypted GLB, then renders it normalised to its category footprint. */
const PropModel: React.FC<{ prop: PlacedProp }> = ({ prop }) => {
    const [url, setUrl] = useState<string | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getStudioPropModelObjectUrl(prop.propId)
            .then((u) => { if (!cancelled) setUrl(u); })
            .catch(() => { if (!cancelled) setFailed(true); });
        return () => { cancelled = true; };
    }, [prop.propId]);

    // A prop that cannot load shows as a wireframe box rather than nothing:
    // an invisible prop the vendor thinks they placed would be captured as
    // empty floor, and the render would "lose" furniture nobody can see.
    if (failed) {
        return (
            <mesh position={[0, 0.4, 0]}>
                <boxGeometry args={[0.8, 0.8, 0.8]} />
                <meshBasicMaterial color="#DC2626" wireframe />
            </mesh>
        );
    }
    if (!url) return null;

    return (
        <Suspense fallback={null}>
            <LoadedProp url={url} category={prop.category} />
        </Suspense>
    );
};

const LoadedProp: React.FC<{ url: string; category: string }> = ({ url, category }) => {
    const { scene } = useGLTF(url) as any;
    const gl = useThree((s) => s.gl);

    const model = useMemo(() => {
        const c = scene.clone(true) as THREE.Group;
        const box = new THREE.Box3().setFromObject(c);
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z) || 1;
        // Normalised the same way as the hero model, but to the category's
        // footprint instead of a fixed 2m — a side table should not arrive
        // sofa-sized.
        c.scale.setScalar(propFootprint(category) / maxDim);
        const box2 = new THREE.Box3().setFromObject(c);
        const center = new THREE.Vector3();
        box2.getCenter(center);
        c.position.x -= center.x;
        c.position.z -= center.z;
        c.position.y -= box2.min.y; // base on the floor

        // Same texture care as the hero: without mipmaps + anisotropy, fabric
        // weaves alias into a wavy mess at grazing angles in the capture.
        const maxAniso = gl.capabilities.getMaxAnisotropy?.() || 1;
        c.traverse((obj: any) => {
            if (!obj.material) return;
            obj.castShadow = true;
            obj.receiveShadow = true;
            const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
            for (const m of mats) {
                const tex = m.map;
                if (tex) {
                    tex.anisotropy = maxAniso;
                    tex.minFilter = THREE.LinearMipmapLinearFilter;
                    tex.generateMipmaps = true;
                    tex.needsUpdate = true;
                }
            }
        });
        return c;
    }, [scene, gl, category]);

    return <primitive object={model} />;
};

/**
 * One placed prop: position/rotation/scale live on the wrapping group, which is
 * what the transform gizmo attaches to — the same contract as a blockout, so
 * the widget treats both selections identically.
 */
export const ScenePropObject: React.FC<{
    prop: PlacedProp;
    selected: boolean;
    capturing: boolean;
    groupRef: (g: THREE.Group | null) => void;
    onSelect: (obj: THREE.Object3D) => void;
}> = ({ prop, selected, capturing, groupRef, onSelect }) => (
    <group
        ref={groupRef}
        position={prop.pos}
        rotation={[0, prop.rotY, 0]}
        scale={prop.scale}
        onClick={(e: any) => { e.stopPropagation(); onSelect(e.eventObject); }}
        onPointerOver={(e: any) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = ''; }}
    >
        <PropModel prop={prop} />
        {selected && !capturing && (
            // A faint ring under the selected prop — the gizmo alone is easy to
            // lose against a busy mesh.
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.01, 0]}>
                <ringGeometry args={[0.55, 0.62, 48]} />
                <meshBasicMaterial color="#3B82F6" transparent opacity={0.6} />
            </mesh>
        )}
    </group>
);

/**
 * How far a placed piece may be taken from its category's natural footprint.
 *
 * Shared with the commit so the handle cannot show a size the state will not
 * keep — the two clamps disagreeing is what makes an object spring back.
 */
export const PROP_SCALE_MIN = 0.3;
export const PROP_SCALE_MAX = 4;

/**
 * One handle, uniform scale.
 *
 * The three-axis scale gizmo was inherited from blockouts, where stretching a
 * grey mass into a wide sofa or a tall lamp WAS the point. A prop is a real
 * mesh: squashing it on one axis makes a chair the AI then has to un-distort,
 * and `PlacedProp.scale` is a single number, so two of the three axes were
 * dragged and then silently discarded on release. The object visibly sprang
 * back. Offering one handle is both the simpler gesture and the honest one —
 * the control now moves exactly the value that is stored.
 *
 * Drag away from the piece to grow, toward it to shrink. Measured in screen
 * pixels from the object's centre rather than along a world axis, so it reads
 * the same from any camera angle and there is no axis to pick first.
 */
export const UniformScaleHandle: React.FC<{
    object: THREE.Object3D;
    onCommit: () => void;
    /** OrbitControls, silenced during a drag so the camera does not chase it. */
    controlsRef: React.MutableRefObject<any>;
}> = ({ object, onCommit, controlsRef }) => {
    const { camera, gl } = useThree();
    const knob = useRef<THREE.Mesh>(null);
    const [hover, setHover] = useState(false);
    const [dragging, setDragging] = useState(false);
    const drag = useRef<{ startDist: number; startScale: number } | null>(null);
    // Reused across frames — this runs every frame on a live scene.
    const scratch = useMemo(() => ({
        box: new THREE.Box3(),
        sphere: new THREE.Sphere(),
        centre: new THREE.Vector3(),
        right: new THREE.Vector3(),
        up: new THREE.Vector3(),
        fwd: new THREE.Vector3(),
        projected: new THREE.Vector3(),
    }), []);

    /** The object's centre in page pixels — the origin every drag measures from. */
    const centreOnScreen = (): { x: number; y: number } | null => {
        const rect = gl.domElement.getBoundingClientRect();
        if (!rect.width || !rect.height) return null;
        scratch.box.setFromObject(object);
        if (scratch.box.isEmpty()) return null;
        scratch.box.getCenter(scratch.projected).project(camera);
        return {
            x: rect.left + ((scratch.projected.x + 1) / 2) * rect.width,
            y: rect.top + ((1 - scratch.projected.y) / 2) * rect.height,
        };
    };

    useFrame(() => {
        const mesh = knob.current;
        if (!mesh) return;
        scratch.box.setFromObject(object);
        if (scratch.box.isEmpty()) { mesh.visible = false; return; }
        mesh.visible = true;
        scratch.box.getCenter(scratch.centre);
        scratch.box.getBoundingSphere(scratch.sphere);
        // Camera-relative, so the handle sits at the piece's top-right whichever
        // way the scene is orbited instead of hiding behind the mesh.
        camera.matrixWorld.extractBasis(scratch.right, scratch.up, scratch.fwd);
        const reach = Math.max(scratch.sphere.radius, 0.25) * 0.9;
        mesh.position.copy(scratch.centre)
            .addScaledVector(scratch.right, reach)
            .addScaledVector(scratch.up, reach);
        // Constant apparent size: a handle that shrinks with distance becomes
        // unclickable on exactly the small pieces that most need resizing.
        mesh.scale.setScalar(camera.position.distanceTo(mesh.position) * 0.016);
    });

    useEffect(() => {
        if (!dragging) return;
        const onMove = (e: PointerEvent) => {
            const d = drag.current;
            const c = centreOnScreen();
            if (!d || !c) return;
            const dist = Math.hypot(e.clientX - c.x, e.clientY - c.y);
            const next = d.startScale * (dist / d.startDist);
            object.scale.setScalar(Math.max(PROP_SCALE_MIN, Math.min(PROP_SCALE_MAX, next)));
        };
        const onUp = () => {
            setDragging(false);
            drag.current = null;
            if (controlsRef.current) controlsRef.current.enabled = true;
            document.body.style.cursor = '';
            onCommit();
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dragging, object]);

    return (
        <mesh
            ref={knob}
            // Swept out of the capture with every other posing aid — this is a
            // bright blue ball floating beside the product, and the one thing a
            // frame handed to an image model must not contain.
            userData={{ studioHelper: true }}
            renderOrder={999}
            onPointerDown={(e: any) => {
                e.stopPropagation();
                const c = centreOnScreen();
                if (!c) return;
                // A grab started on top of the centre would divide by ~0 and send
                // the scale to infinity on the first pixel of movement.
                const startDist = Math.max(12, Math.hypot(e.clientX - c.x, e.clientY - c.y));
                drag.current = { startDist, startScale: object.scale.x };
                if (controlsRef.current) controlsRef.current.enabled = false;
                document.body.style.cursor = 'nwse-resize';
                setDragging(true);
            }}
            onPointerOver={(e: any) => { e.stopPropagation(); setHover(true); document.body.style.cursor = 'nwse-resize'; }}
            onPointerOut={() => { setHover(false); if (!dragging) document.body.style.cursor = ''; }}
        >
            <sphereGeometry args={[0.5, 20, 20]} />
            {/* Depth-test off so it stays grabbable when the piece is in front of
                it — a handle you can see but not click is worse than none. */}
            <meshBasicMaterial color={hover || dragging ? '#2563EB' : '#3B82F6'} depthTest={false} toneMapped={false} />
        </mesh>
    );
};

/**
 * Turns a drop on the canvas into a floor position.
 *
 * Dragging a chair onto the scene should put it where it was dropped, not at a
 * guessed offset — but the maths needs the live camera, which only exists
 * inside the R3F tree. This mounts there and hands a resolver up through a ref,
 * so the DOM drop handler outside can ask "what floor point is this pixel?".
 */
export const FloorDropResolver: React.FC<{
    resolverRef: React.MutableRefObject<((clientX: number, clientY: number) => [number, number, number] | null) | null>;
}> = ({ resolverRef }) => {
    const { camera, gl } = useThree();

    useEffect(() => {
        const raycaster = new THREE.Raycaster();
        const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
        const point = new THREE.Vector3();

        resolverRef.current = (clientX, clientY) => {
            const rect = gl.domElement.getBoundingClientRect();
            if (!rect.width || !rect.height) return null;
            const ndc = new THREE.Vector2(
                ((clientX - rect.left) / rect.width) * 2 - 1,
                -((clientY - rect.top) / rect.height) * 2 + 1,
            );
            raycaster.setFromCamera(ndc, camera);
            if (!raycaster.ray.intersectPlane(floor, point)) return null;
            // Kept on the visible floor: a drop near the horizon solves to a
            // point hundreds of metres away, which reads as "it vanished".
            const clamp = (v: number) => Math.max(-6, Math.min(6, v));
            return [clamp(point.x), 0, clamp(point.z)];
        };

        return () => { resolverRef.current = null; };
    }, [camera, gl, resolverRef]);

    return null;
};
